import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { checkSelectedAssets } from "./utils";
import { exportQRSpreadsheet } from "./xlsx/xlsx";
import { getAssetsById } from "./api";
import { Box, Paper, useTheme } from "@mui/material";
import { isEmpty, isEqual } from "lodash";
import { Redirect } from "react-router-dom";
import { thunks } from "../../../globalStore/slices/inventory/inventorySlice";
import { useDispatch, useSelector } from "react-redux";
import AssignmentReturnedIcon from "@mui/icons-material/AssignmentReturned";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import Grid from "@mui/material/Grid2";
import HoverIconButton from "../../../components/ReusedComponents/HoverIconButton";
import InventoryMapFilter from "./InventoryMapFilter";
import InventoryStatusMap from "./Map/InventoryStatusMap";
import LaptopIcon from "@mui/icons-material/Laptop";
import Loading from "../../../components/Loading/Loading";
import MapIcon from "@mui/icons-material/Map";
import MapTable from "./Map/MapTable";
import ModalDialog from "../../../components/Modals/ModalDialog/ModalDialog";
import NotificationModal from "../../../components/Modals/NotificationModal";
import QrCodeIcon from "@mui/icons-material/QrCode";
import TableChartIcon from "@mui/icons-material/TableChart";
import { getClassificationsByType } from "../../../utils/getClassificationsByType";

// Components are super heavy, which is why we are using lazy/Suspense
const AssignDevice = lazy(() =>
  import("../../assets/assignDevice/AssignDevice")
);
const ImportAssets = lazy(() =>
  import("../../assets/ImportAssets/ImportAssets")
);
const Table = lazy(() => import("./Table/Table"));

export default function InventoryStatus(props) {
  const theme = useTheme();
  const isDark = theme.palette?.mode === "dark";
  
  const classes2 = {
    button: {
      marginLeft: "1rem",
      marginBottom: ".25rem",
    },
    buttonContainer: {
      textAlign: "right",
      marginBottom: "1rem",
    },
    paper: {
      minHeight: "68vh",
      padding: "20px",
      textAlign: "center",
      borderRadius: "16px",
      boxShadow: isDark ? "0 4px 24px rgba(0,0,0,0.4)" : "0 4px 20px rgba(0,0,0,0.08)",
      backgroundColor: isDark ? "#032d42" : "#ffffff",
      border: isDark ? "1px solid rgba(160, 196, 109, 0.15)" : "none",
    },
    map: (themeArg) => ({
      padding: themeArg.spacing(2),
      minHeight: "62vh",
      maxHeight: "62vh",
      borderRadius: "12px",
      overflow: "hidden",
    }),
    mapTable: (themeArg) => ({
      padding: themeArg.spacing(2),
      minHeight: "62vh",
      maxHeight: "62vh",
      overflow: "auto",
      borderRadius: "12px",
      backgroundColor: isDark ? "#053350" : "#fafafa",
      boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "none",
      border: isDark ? "1px solid rgba(160, 196, 109, 0.2)" : "none",
    }),
  };

  const dispatchGlobal = useDispatch();
  const saveFilterSettings = thunks.assets.saveFilterSettings;
  const showLastEvents = thunks.assets.showLastEvents;

  const {
    apiUrl,
    organizationId,
    timeZone,
    token,
    userId,
    usersConsoleRole,
  } = props;

  // OK to destructure, as no changes to org data takes place in this page
  const {
    organization,
    zones,
    facilities,
    customs,
    devices,
    usersMap,
    products,
  } = useSelector((state) => state.organization, isEqual);

  let inventoryStatusClassifications = getClassificationsByType({
    assetMode: "Inventory",
    isObject: true
  });

  const inventoryCustoms = customs.filter(
    (custom) => custom.assetMode === "Inventory"
  );

  const assetTypes =
    organization && organization.assetTypes
      ? [...organization.assetTypes].sort()
      : [];

  const eventTypes =
    organization && organization.eventTypesMap
      ? Object.keys(organization.eventTypesMap).sort()
      : [];

  const facilityArray = facilities
    ? Object.values(facilities).sort((a, b) => {
      return (a.name || "").localeCompare(b.name || "");
    })
    : [];

  const allDevices = Object.keys(devices).map((k) => devices[k]);
  //the new indicator is hasDescendants. if it hasDescendants, it is assigned.
  //still keeping the old indicator for devices created before the hasDescendants field was added
  const availableDevices = allDevices.filter(
    (item) => item?.device && (item.device.hasDescendents || item.device.status !== "assigned")
  );

  const assetsStore = useSelector((state) => state.assets, isEqual);

  const { filters: initFilters = {} } = assetsStore;

  const [state, setState] = useState({
    allDevices,
    availableDevices,
    availableZones: Object.keys(zones).map((zone) => {
      // TODO: Is this how we are going to handle the differences between
      // pZones and tZones?
      return {
        internalZoneType: zones[zone].internalZoneType || null,
        label: zones[zone].name,
        value: zones[zone].zoneId,
      };
    }),
    count: assetsStore.count,
    filters: {
      ...initFilters,
      startDate: new Date(),
      endDate: new Date(),
    },
    lists: {
      assetTypes: assetTypes,
      eventTypes: eventTypes,
      eventTypesMap: organization.eventTypesMap || {},
      facilityArray: facilityArray,
      assetCategoriesList:
        organization.assetCategoriesList || organization.assetCategories || [],
    },
    mapTableData: [],
    mounted: true,
    page: 0,
    parentAssetIdTagMap: {},
    selectedAssets: {},
  });

  const [loading, setLoading] = useState(false);

  const [modal, setModal] = useState({
    modalShow: false,
    title: "",
    content: {},
  });

  const modalClose = () =>
    setModal({ modalShow: false, title: "", content: "" });

  const [dialog, setDialog] = useState({
    dialogShow: false,
  });

  const [isRedirect, setIsRedirect] = useState(false);

  const [confirmationModalShow, setConfirmationModalShow] = useState({
    error: false,
    modalShow: false,
    text: "",
  });

  const [importAssetsModalTitle, setImportAssetsModalTitle] =
    useState("Import Inventory");

  const isMountedRef = useRef(false);

  // upon first mount, this function will check for updates in the store by dispatching the showLastEvents thunk
  const init = useCallback(
    (isMounted) => {
      if (!isMounted) {
        dispatchGlobal(
          showLastEvents({ filters: state.filters, page: state.page })
        ).then((res) => {
          isMountedRef.current = true;
          setState((prevState) => ({
            ...prevState,
            assets: res?.payload?.assets || [],
            parentAssetIdTagMap: res?.payload?.parentAssetIdTagMap || {},
          }));
        });
      }
    },
    [dispatchGlobal, showLastEvents, state]
  );

  // intializing effect, this is for checking for updated asset status on first mount using initial filters. Using a ref to ensure it only fires on first mount.
  useEffect(() => {
    init(isMountedRef.current);
  }, [init]);

  // this effect fires whenever filters are updated (either in the filter menu or by changing the page or row limit).
  // it checks the isMountedRef to prevent firing on first mount, sets loading to true, and then dispatches the showLastEvents thunk to our store to searchAssets and update state
  // it then takes the return from our showLastEvents thunk and updates the local state
  useEffect(() => {
    if (isMountedRef.current) {
      setLoading(true);
      dispatchGlobal(
        showLastEvents({
          filters: state.filters,
          page: state.page,
          useFilters: false,
        })
      ).then((results) => {
        setLoading(false);
        setState((prevState) => {
          const limit = state.filters.limit || 1; // Prevent division by zero
          return {
            ...prevState,
            assets: results?.payload?.assets || [],
            count: results?.payload?.count || 0,
            page: Math.floor(state.filters.start / limit),
            parentAssetIdTagMap: results?.payload?.parentAssetIdTagMap || {},
            // reset map table data
            mapTableData: [],
          };
        });
      });
    }
  }, [dispatchGlobal, showLastEvents, state.filters, state.page]);

  const handleSave = async (filters) => {
    // Removes the start from saving to the users settings
    delete filters.start;

    return await dispatchGlobal(saveFilterSettings(filters));
  };

  function handleRowSelect(selectedRowIds) {
    let updatedSelectedAssets = {};
    const assetsList = state.assets;

    // go through the selectedRowIds and find the asset that matches the id. If that asset exists, add it to the updatedSelectedAssets object
    for (let rowId of selectedRowIds) {
      for (let asset of assetsList) {
        if (asset.assetId === rowId) {
          updatedSelectedAssets = {
            ...updatedSelectedAssets,
            [asset.assetId]: asset,
          };
        }
      }
    }

    setState((s) => {
      return {
        ...s,
        selectedAssets: updatedSelectedAssets,
      };
    });
  }

  async function checkDevices() {
    let isValid = false;
    let assets;
    let assetIds = [
      ...new Set(
        Object.keys(state.selectedAssets).map((asset) => {
          return asset;
        })
      ),
    ];

    // Retrieve up to date asset information
    assets = await getAssetsById(
      { apiUrl, token, organizationId },
      assetIds,
      1000
    );

    // Checks to verify selected asset(s) do not currently have a device associated
    const hasParentId = assets.assets.find((element) => element.parentId);

    // Checks to verify they actually have enough devices for the amount of selected assets
    const hasEnoughDevices =
      state.availableDevices.length < Object.keys(state.selectedAssets).length;

    if (hasEnoughDevices) {
      setConfirmationModalShow({
        modalShow: true,
        text: "You have selected more assets than there are available devices. Please adjust your selections.",
        error: true,
      });
    } else if (hasParentId) {
      setConfirmationModalShow({
        modalShow: true,
        text: "You have selected one or more assets that already has a device assigned to it.",
        error: true,
      });
    } else isValid = true;

    if (isValid) {
      setModal({
        modalShow: true,
        title: "Assign a Tracker",
        content: (
          <Suspense fallback={<Loading />}>
            <AssignDevice
              allDevices={state.allDevices}
              apiUrl={apiUrl}
              availableDevices={state.availableDevices}
              eventTypesMap={state.lists.eventTypesMap}
              organizationId={organizationId}
              selectedAssets={state.selectedAssets}
              setConfirmationModalShow={setConfirmationModalShow}
              setModal={setModal}
              timeZone={timeZone}
              token={token}
            />
          </Suspense>
        ),
      });
    }
  }

  function toolBarButtons() {
    const hasSelectedAssets = !isEmpty(state.selectedAssets);
    const selectedCount = Object.keys(state.selectedAssets).length;

    return (
      <Grid
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
        }}
      >
        {/* View Toggle */}
        <HoverIconButton
          icon={<MapIcon />}
          iconDirection="right"
          handleClick={() => {
            setState((s) => {
              return {
                ...s,
                displayMap: !s.displayMap,
              };
            });
          }}
          text={state.displayMap ? `View Table` : `View Map`}
          variant="primary"
        />

        {state.displayMap ? null : (
          <>
            {/* Divider */}
            <Box
              sx={{
                width: "1px",
                height: "28px",
                backgroundColor: "rgba(148, 163, 184, 0.2)",
                marginX: "6px",
                borderRadius: 1,
              }}
            />

            {/* Selection Actions Group */}
            <HoverIconButton
              disabled={!hasSelectedAssets}
              text="Assign Tracker"
              icon={<LaptopIcon />}
              iconDirection="right"
              handleClick={() => {
                if (hasSelectedAssets) {
                  checkDevices();
                }
              }}
              variant="secondary"
            />

            <HoverIconButton
              text="Generate CQRs"
              icon={<QrCodeIcon />}
              iconDirection="right"
              disabled={!hasSelectedAssets}
              handleClick={() => {
                if (hasSelectedAssets) {
                  checkSelectedAssets(
                    { organizationId, apiUrl, token },
                    state
                  ).then((results) => {
                    const { faultyContent = [], isError = true } = results;
                    if (!isError) {
                      setIsRedirect(true);
                    } else {
                      setConfirmationModalShow({
                        modalShow: true,
                        text: `The Asset Tag(s)${faultyContent.map((item) => {
                          return ` ${item}`;
                        })} are shown as still being in circulation, therefore the CQR cannot be regenerated. If you need to reprint a CQR for an asset, please contact your organizations Admin User.`,
                        error: true,
                      });
                    }
                  });
                }
              }}
              variant="secondary"
            />

            <HoverIconButton
              text="Export QR Urls"
              icon={<ContentPasteGoIcon />}
              iconDirection="right"
              disabled={!hasSelectedAssets}
              handleClick={() => {
                exportQRSpreadsheet(
                  Object.values(state.selectedAssets),
                  token,
                  "Inv_Status"
                );
              }}
              variant="secondary"
            />

            {/* Divider */}
            <Box
              sx={{
                width: "1px",
                height: "28px",
                backgroundColor: "rgba(148, 163, 184, 0.2)",
                marginX: "6px",
                borderRadius: 1,
              }}
            />

            {/* Import Action */}
            <HoverIconButton
              text="Import"
              icon={<AssignmentReturnedIcon />}
              iconDirection="right"
              handleClick={() => {
                setDialog({ dialogShow: true });
              }}
              disabled={false}
              variant="success"
            />

            {/* Selected Count Badge */}
            {hasSelectedAssets && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 14px",
                  borderRadius: "10px",
                  backgroundColor: isDark ? "rgba(160, 196, 109, 0.15)" : "#f0fdf4",
                  border: isDark ? "1px solid rgba(160, 196, 109, 0.4)" : "1px solid #86efac",
                  color: isDark ? "#A0C46D" : "#166534",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  animation: "fadeIn 0.2s ease-out",
                  "@keyframes fadeIn": {
                    from: { opacity: 0, transform: "scale(0.95)" },
                    to: { opacity: 1, transform: "scale(1)" },
                  },
                }}
              >
                <Box
                  sx={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    backgroundColor: isDark ? "#A0C46D" : "#22c55e",
                    color: isDark ? "#1e293b" : "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                  }}
                >
                  {selectedCount}
                </Box>
                selected
              </Box>
            )}
          </>
        )}
      </Grid>
    );
  }

  return (
    <Grid
      className="inventory-status"
      container
      style={{
        marginTop: "1rem",
        width: "84vw",
        margin: "0 auto",
      }}
    >
      {isRedirect ? (
        <Redirect
          to={{
            pathname: "/generateCQR/",
            state: { selectedAssets: state.selectedAssets },
          }}
        />
      ) : (
        ""
      )}
      <ModalDialog
        handleClose={modalClose}
        open={modal.modalShow}
        title={modal.title}
        content={
          <Grid container>
            <Grid size={12}>{modal.content}</Grid>
          </Grid>
        }
      />
      <ModalDialog
        handleClose={() => {
          setDialog({
            dialogShow: false,
          });
        }}
        open={dialog.dialogShow}
        title={importAssetsModalTitle}
        content={
          <Suspense fallback={<Loading />}>
            <ImportAssets
              apiUrl={apiUrl}
              availableDevices={state.availableDevices}
              classifications={inventoryStatusClassifications || {}}
              customs={inventoryCustoms}
              facilities={facilities}
              isInventory={true}
              onHide={() => {
                setDialog({
                  dialogShow: false,
                });
                setImportAssetsModalTitle("Import Assets");
              }}
              onSuccess={() => {
                // force refetch via useEffect
                setState({
                  ...state,
                  filters: {
                    ...state.filters,
                  },
                });
              }}
              organization={organization}
              setConfirm={setConfirmationModalShow}
              setImportAssetsModalTitle={setImportAssetsModalTitle}
              token={token}
            />
          </Suspense>
        }
      />

      <NotificationModal
        confirmationModalShow={confirmationModalShow.modalShow}
        modalClose={() => {
          setConfirmationModalShow({
            error: false,
            modalShow: false,
            text: "",
          });
        }}
        confirmationText={confirmationModalShow.text}
        color={confirmationModalShow.error ? "error" : "success"}
      />
      {state.displayMap ? (
        <Grid sx={classes2.buttonContainer}>
          <HoverIconButton
            handleClick={() => {
              setState((s) => {
                return {
                  ...s,
                  displayMap: !s.displayMap,
                };
              });
            }}
            icon={<TableChartIcon />}
            icondirection="right"
            text="View Status Table"
          />
        </Grid>
      ) : null}
      <Grid size={12}>
        {state.filters ? (
          <>
            {state.displayMap ? (
              <>
                <Paper sx={classes2.paper}>
                  <Grid>
                    <Grid size={12}>
                      <InventoryMapFilter
                        apiUrl={apiUrl}
                        isAssetAnalysis={true}
                        setState={setState}
                        state={state}
                        organizationId={organizationId}
                        token={token}
                      />
                    </Grid>
                  </Grid>

                  <p style={{ marginTop: "10px" }}>
                    Click marker, then "See Data" to view multiple assets from
                    the same location in the table below.
                  </p>
                  <Grid container spacing={3}>
                    <Grid
                      sx={classes2.map}
                      size={{
                        sm: 6,
                        xs: 12,
                      }}
                    >
                      <InventoryStatusMap
                        apiUrl={apiUrl}
                        classifications={inventoryStatusClassifications}
                        dispatchGlobal={dispatchGlobal}
                        eventTypesMap={state.lists.eventTypesMap}
                        facilities={facilities}
                        organizationId={organizationId}
                        setConfirm={setConfirmationModalShow}
                        setLoading={setLoading}
                        setState={setState}
                        showLastEvents={showLastEvents}
                        state={state}
                        timeZone={timeZone}
                        token={token}
                      />
                    </Grid>
                    <Grid
                      sx={classes2.mapTable}
                      size={{
                        sm: 6,
                        xs: 12,
                      }}
                    >
                      <MapTable
                        mapTableData={state.mapTableData}
                        timeZone={state.filters.tz ? state.filters.tz : ""}
                        facilities={facilities}
                        zones={zones}
                        setState={setState}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </>
            ) : (
              <Suspense fallback={<Loading />}>
                <Table
                  allDevices={state.allDevices}
                  apiUrl={apiUrl}
                  checkDevices={checkDevices}
                  classifications={inventoryStatusClassifications}
                  customs={customs}
                  defaultColumnOrder={state.filters?.defaultColumnOrder}
                  dispatchGlobal={dispatchGlobal}
                  facilities={facilities}
                  handleRowSelect={handleRowSelect}
                  handleSave={handleSave}
                  loading={loading}
                  organizationId={organizationId}
                  page={state.page}
                  parentAssetIdTagMap={state.parentAssetIdTagMap}
                  parentIds={assetsStore.parentIds}
                  products={products}
                  setConfirm={setConfirmationModalShow}
                  setLoading={setLoading}
                  setModal={setModal}
                  setState={setState}
                  showLastEvents={showLastEvents}
                  state={state}
                  timeZone={timeZone}
                  token={token}
                  toolBarButtons={toolBarButtons}
                  userId={userId}
                  usersConsoleRole={usersConsoleRole}
                  usersMap={usersMap}
                  useSelector={useSelector}
                  zones={zones}
                />
              </Suspense>
            )}
          </>
        ) : null}
      </Grid>
    </Grid>
  );
}
